import { useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Check, Lock, Plus, Trash2, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { SLA_SCOPES, type ApprovalChainView, type ChainAppliesTo, type ChainApproverChoices, type SlaRuleView } from '@nest-recruit/shared';
import { Avatar } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Input, SearchInput } from '../../../components/Input';
import { PersonPicker } from '../../../components/PersonPicker';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { Switch } from '../../../components/Switch';
import { Tabs } from '../../../components/Tabs';
import { EmptyState, FilteredEmptyState, ForbiddenState, ServerErrorState, SwitchedOffState } from '../../../components/system-states/SystemState';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { useModules } from '../../../lib/modules';
import { useUnsavedWarning } from '../../../lib/use-unsaved-warning';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useDepartments, useVentures } from '../../organisation/api/organisation-api';
import {
  addApprover,
  addStep,
  chainBody,
  chainMatches,
  chainScope,
  draftFrom,
  isSameChain,
  moveStep,
  newChain,
  removeApprover,
  removeStep,
  replaceApprover,
  replaceStep,
  stepNumberOf,
  stepRepeatsHrCheck,
  whatIsMissing,
  type DraftApprover,
  type DraftChain,
  type DraftStep,
} from '../approval-chain-draft';
import { useApprovalChains, useChainApprovers, useSaveApprovalChain, useSlaRules } from '../api/requisitions-api';
import { formatActive } from '../sla-format';
import styles from './ApprovalChainsPage.module.css';

/**
 * ADMIN-06 — approval chains (F19.06), option A of `4-messages/approval-chains.html` (Admin redesign 2026-11-02): the chains on the
 * left; the chosen chain drawn as a flow (sent, each step, approved), with parallel approvers stacked inside their step; the chosen
 * step edited underneath.
 *
 * §L41 (owner, 2026-11-02 Q1 a): every hire request starts with HR's check, drawn here as a locked first card; a chain's steps replace
 * only the Leadership step after it. A step that only asks HR again is skipped, and the page says so. Offer chains are Offers' (they
 * show whenever Offers is on, even with Approval chains off), with the "Outside the pay band" step after the last one. Grade is gone.
 * Time limits is its own page now; while it is on, the overall limit for each step is shown here read-only.
 *
 * Editing a chain never touches requests already in approval: they were copied from the version they started with (#4). Each chain
 * keeps its own unsaved draft while another is open, so switching never loses work; the browser warns before leaving with any.
 */

export type ChainTab = ChainAppliesTo;

export interface ApprovalChainsSearch {
  readonly tab?: ChainTab;
  /** A chain's id, or `new`. */
  readonly chain?: string;
}

export function readApprovalChainsSearch(search: Readonly<Record<string, unknown>>): ApprovalChainsSearch {
  const tab = search.tab === 'offer' || search.tab === 'requisition' ? search.tab : undefined;
  const chain = typeof search.chain === 'string' && search.chain !== '' ? search.chain : undefined;
  return { ...(tab ? { tab } : {}), ...(chain ? { chain } : {}) };
}

const TAB_LABELS: Readonly<Record<ChainTab, string>> = { requisition: 'Requisition chains', offer: 'Offer chains' };
const NEW = 'new';

/** One saved chain's draft: what it was when editing started (the version Save sends) and what it is now. */
interface Edit {
  readonly base: ApprovalChainView | null;
  readonly draft: DraftChain;
}

const draftKey = (tab: ChainTab, chainId: string): string => (chainId === NEW ? `new:${tab}` : chainId);

export function ApprovalChainsPage(): ReactNode {
  const canView = useCanView('admin_config');
  const canEdit = useCanEdit('admin_config');
  const modules = useModules();
  const navigate = useNavigate();
  const search = readApprovalChainsSearch(useSearch({ strict: false }) as Readonly<Record<string, unknown>>);
  // Hire-request chains need Approval chains and Hire requests; offer chains need Offers (§L34). A tab of a part that is off isn't drawn.
  const tabs: readonly ChainTab[] = [...(modules.approvals && modules.requisitions ? (['requisition'] as const) : []), ...(modules.offers ? (['offer'] as const) : [])];
  const chains = useApprovalChains(canView && tabs.length > 0);
  const [edits, setEdits] = useState<Readonly<Record<string, Edit>>>({});
  const [query, setQuery] = useState('');
  useUnsavedWarning(Object.keys(edits).length > 0);

  if (!canView) return <ForbiddenState resource="approval chains" contact="Your admin" />;
  if (tabs.length === 0) return <SwitchedOffState feature="Approval chains" canOpenSettings={canEdit} />;

  const tab: ChainTab = search.tab && tabs.includes(search.tab) ? search.tab : (tabs[0] ?? 'requisition');
  const all = chains.data ?? [];
  const ofTab = all.filter((chain) => chain.appliesTo === tab);
  const shown = ofTab.filter((chain) => chainMatches(chain, query));
  const go = (next: ApprovalChainsSearch): void => void navigate({ to: '/admin/approval-chains', search: next, replace: true });
  const chainId = search.chain ?? shown[0]?.id ?? null;
  const base = chainId && chainId !== NEW ? (ofTab.find((chain) => chain.id === chainId) ?? null) : null;
  const key = chainId ? draftKey(tab, chainId) : null;
  const edit = key ? edits[key] : undefined;

  const setDraft = (draft: DraftChain): void => {
    if (!key) return;
    const start = edit?.base ?? base;
    const clean = start ? draftFrom(start) : null;
    setEdits((current) => {
      const next = { ...current };
      // Back to what is saved: the draft goes, so the chain follows the server again.
      if (clean && isSameChain(clean, draft)) delete next[key];
      else next[key] = { base: start, draft };
      return next;
    });
  };
  const dropDraft = (draftKeyToDrop: string): void =>
    setEdits((current) => {
      const next = { ...current };
      delete next[draftKeyToDrop];
      return next;
    });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>Approval chains</h1>
          <p className={styles.lede}>
            {tab === 'requisition'
              ? "Who approves a hire request after HR's check. Every request starts with HR, who can fix anything unclear; a chain replaces the Leadership step that follows. The department's own chain wins over the whole venture's. Requests already in approval keep the steps they started with."
              : 'Who approves an offer before it is sent. Each job chooses its offer chain in its offer settings, and an offer outside the pay band gets one more step. Offers already in approval keep the steps they started with.'}
          </p>
        </div>
        {canEdit && (
          <Button variant="solid" icon={Plus} onClick={() => go({ tab, chain: NEW })}>
            New chain
          </Button>
        )}
      </header>

      <Tabs<ChainTab>
        label="Kinds of chain"
        tabs={tabs.map((value) => {
          const count = all.filter((chain) => chain.appliesTo === value).length;
          return { value, label: TAB_LABELS[value], after: chains.data ? <span className={styles.count}>{count}</span> : undefined, afterLabel: chains.data ? `${count} chains` : undefined };
        })}
        value={tab}
        onChange={(next) => go({ tab: next })}
      >
        {chains.isPending ? (
          <div className={styles.panel}>
            <SkeletonLines count={6} />
          </div>
        ) : chains.isError ? (
          <div className={styles.panel}>
            <ServerErrorState requestId={chains.error.requestId} onRetry={() => void chains.refetch()} />
          </div>
        ) : ofTab.length === 0 && chainId !== NEW ? (
          <div className={styles.panel}>
            <EmptyState
              title="No chains yet"
              body={
                tab === 'requisition'
                  ? "Without a chain, every hire request goes to HR's check, then Leadership. Add one to send a venture's or a department's requests to its own approvers after HR."
                  : "A job can't send an offer for approval without an offer chain. Add one for each venture, for example Finance head, then HR."
              }
              action={
                canEdit ? (
                  <Button variant="solid" icon={Plus} onClick={() => go({ tab, chain: NEW })}>
                    New chain
                  </Button>
                ) : null
              }
            />
          </div>
        ) : (
          <div className={styles.layout}>
            <ChainList
              tab={tab}
              chains={shown}
              total={ofTab.length}
              query={query}
              onQuery={setQuery}
              selected={chainId}
              unsaved={new Set(Object.keys(edits))}
              hasNew={edits[draftKey(tab, NEW)] !== undefined || chainId === NEW}
              onPick={(id) => go({ tab, chain: id })}
            />
            {chainId === null ? (
              <FilteredEmptyState onClearFilters={() => setQuery('')} filterCount={1} isInset />
            ) : chainId !== NEW && !base ? (
              <Card title="That chain isn't here">
                <p className={styles.muted}>It may have been removed, or it approves {tab === 'offer' ? 'hire requests' : 'offers'}. Pick one from the list.</p>
              </Card>
            ) : (
              <ChainWorkspace
                key={key ?? 'none'}
                tab={tab}
                saved={base}
                start={edit?.base ?? base}
                draft={edit?.draft ?? (base ? draftFrom(base) : newChain(tab, null))}
                canEdit={canEdit}
                onChange={setDraft}
                onDiscard={() => {
                  if (key) dropDraft(key);
                  if (chainId === NEW) go({ tab });
                }}
                onSaved={(id) => {
                  if (key) dropDraft(key);
                  go({ tab, chain: id });
                }}
              />
            )}
          </div>
        )}
      </Tabs>
    </div>
  );
}

// --- The list on the left -------------------------------------------------------------------------------------------

function ChainList({
  tab,
  chains,
  total,
  query,
  onQuery,
  selected,
  unsaved,
  hasNew,
  onPick,
}: {
  readonly tab: ChainTab;
  readonly chains: readonly ApprovalChainView[];
  readonly total: number;
  readonly query: string;
  readonly onQuery: (query: string) => void;
  readonly selected: string | null;
  readonly unsaved: ReadonlySet<string>;
  readonly hasNew: boolean;
  readonly onPick: (id: string) => void;
}): ReactNode {
  return (
    <section className={styles.list} aria-label={TAB_LABELS[tab]}>
      {total > 1 && <SearchInput placeholder="Find a chain" aria-label="Find a chain" value={query} onChange={(event) => onQuery(event.target.value)} onClear={() => onQuery('')} />}
      <ul className={styles.items}>
        {hasNew && (
          <li>
            <button type="button" className={styles.item} aria-current={selected === NEW || undefined} onClick={() => onPick(NEW)}>
              <span className={styles.itemName}>New chain</span>
              <span className={styles.itemMeta}>Not saved yet</span>
            </button>
          </li>
        )}
        {chains.map((chain) => (
          <li key={chain.id}>
            <button type="button" className={styles.item} aria-current={selected === chain.id || undefined} onClick={() => onPick(chain.id)}>
              <span className={styles.itemTop}>
                <span className={styles.itemName}>{chain.name}</span>
                {!chain.isActive && (
                  <Chip tone="neutral" plain>
                    Off
                  </Chip>
                )}
                {unsaved.has(chain.id) && <Chip tone="attention">Unsaved</Chip>}
              </span>
              <span className={styles.itemMeta}>{chainScope(chain)}</span>
              <span className={styles.itemMeta}>
                {chain.steps.length} {chain.steps.length === 1 ? 'step' : 'steps'}
                {tab === 'requisition' ? ' after the HR check' : ''}
              </span>
            </button>
          </li>
        ))}
        {chains.length === 0 && query.trim() !== '' && <li className={styles.itemMeta}>No chain matches “{query.trim()}”.</li>}
      </ul>
      <p className={styles.listNote}>
        {tab === 'requisition' ? "Without a chain: HR's check, then Leadership." : 'A job chooses its offer chain in its offer settings.'}
      </p>
    </section>
  );
}

// --- The chosen chain -----------------------------------------------------------------------------------------------

type Selected = { readonly kind: 'hr' } | { readonly kind: 'step'; readonly index: number } | { readonly kind: 'extra' };

function ChainWorkspace({
  tab,
  saved,
  start,
  draft,
  canEdit,
  onChange,
  onDiscard,
  onSaved,
}: {
  readonly tab: ChainTab;
  /** The chain as the server has it now; null for a new one. */
  readonly saved: ApprovalChainView | null;
  /** The chain as it was when editing started: its version is the one Save sends (two Admins at once). */
  readonly start: ApprovalChainView | null;
  readonly draft: DraftChain;
  readonly canEdit: boolean;
  readonly onChange: (draft: DraftChain) => void;
  readonly onDiscard: () => void;
  readonly onSaved: (id: string) => void;
}): ReactNode {
  const modules = useModules();
  const ventures = useVentures();
  const departments = useDepartments();
  // Read-only viewers load them too, so an approver who can't decide is flagged for them as well.
  const approvers = useChainApprovers(tab);
  const rules = useSlaRules(tab === 'requisition' && modules.sla);
  const save = useSaveApprovalChain();
  const show = useToasts((state) => state.show);
  const [selected, setSelected] = useState<Selected>({ kind: 'step', index: 0 });

  const isNew = draft.id === null;
  const isDirty = isNew || (start !== null && !isSameChain(draftFrom(start), draft));
  // Someone else saved this chain after this draft started: Save would overwrite them, so it waits until they choose.
  // Not while our own save is landing: its answer is the newer version, and the draft goes the moment it lands.
  const isStale = !isNew && !save.isPending && saved !== null && start !== null && saved.version !== start.version;
  const liveVentures = (ventures.data ?? []).filter((venture) => venture.archivedAt === null);
  const choices = approvers.data ?? null;
  // §L41's HR role, from the choices, or until they load from a step the server already marked as repeating the HR check.
  const hrCheckRoleId = choices?.hrCheckRoleId ?? hrRoleOf(saved ?? start);

  // A new chain in a company with one venture starts in it.
  const ventureId = draft.ventureId ?? (isNew && liveVentures.length === 1 ? (liveVentures[0]?.id ?? null) : null);
  const current: DraftChain = ventureId === draft.ventureId ? draft : { ...draft, ventureId };
  const missing = whatIsMissing(current);
  const ventureDepartments = (departments.data ?? []).filter((department) => department.ventureId === current.ventureId && department.archivedAt === null);
  const limit = stepLimit(rules.data, current.ventureId);

  async function submit(): Promise<void> {
    try {
      const body = chainBody(current);
      const { id } = await save.mutateAsync({ id: current.id, version: start?.version ?? 0, ...body });
      show({
        tone: 'success',
        message: `${body.name} saved`,
        detail: tab === 'requisition' ? 'New requests use it from now on. Requests already in approval keep their steps.' : 'Offers sent for approval from now on use it.',
      });
      onSaved(id);
    } catch (cause) {
      const isConflict = cause instanceof ApiError && cause.status === 409;
      show({
        tone: 'danger',
        message: 'The chain was not saved',
        detail: isConflict ? 'Someone else saved this chain meanwhile. Your changes are kept here; load theirs to start again from them.' : errorMessage(cause),
      });
    }
  }

  const readOnly = !canEdit;
  // After a discard or a removal the chosen step may be past the end: the last one stands in.
  const stepIndex = selected.kind === 'step' ? Math.min(selected.index, current.steps.length - 1) : -1;
  const stepAt = stepIndex >= 0 ? current.steps[stepIndex] : undefined;

  return (
    <div className={styles.workspace}>
      <Card>
        <div className={styles.scope}>
          <Field label="Name" isRequired>
            {({ inputId }) => (
              <Input id={inputId} value={current.name} disabled={readOnly} placeholder="Nest Foods senior hires" onChange={(event) => onChange({ ...current, name: event.target.value })} />
            )}
          </Field>
          <Field label="Venture" isRequired>
            {({ inputId }) => (
              <Select
                id={inputId}
                label="Venture"
                placeholder="Choose a venture"
                disabled={readOnly}
                value={current.ventureId}
                options={withSaved(
                  liveVentures.map((venture) => ({ value: venture.id, label: venture.name })),
                  current.ventureId,
                  saved?.ventureName,
                )}
                onChange={(next) => onChange({ ...current, ventureId: next, departmentId: null })}
              />
            )}
          </Field>
          {tab === 'requisition' && (
            <Field label="Department" isOptional>
              {({ inputId }) => (
                <Select
                  id={inputId}
                  label="Department"
                  placeholder="Every department"
                  disabled={readOnly || current.ventureId === null}
                  value={current.departmentId ?? ''}
                  options={[
                    { value: '', label: 'Every department' },
                    ...withSaved(
                      ventureDepartments.map((department) => ({ value: department.id, label: department.name })),
                      current.departmentId,
                      saved?.departmentName ?? undefined,
                    ),
                  ]}
                  onChange={(next) => onChange({ ...current, departmentId: next === '' ? null : next })}
                />
              )}
            </Field>
          )}
          <div className={styles.inUse}>
            <Switch
              checked={current.isActive}
              disabled={readOnly}
              label={tab === 'requisition' ? 'New requests can use this chain' : 'Jobs can choose this chain for their offers'}
              onChange={(isActive) => onChange({ ...current, isActive })}
            />
          </div>
        </div>
      </Card>

      <section className={styles.flowCard} aria-label="The chain, step by step">
        <p className={styles.flowLabel}>
          {tab === 'requisition' ? 'Starts when a request is sent' : 'Starts when an offer is sent for approval'}
          {limit && <span className={styles.flowLimit}> · each step has up to {limit} (Time limits)</span>}
        </p>
        <ol className={styles.flow}>
          <li className={styles.flowItem}>
            <span className={styles.endpoint}>Sent</span>
          </li>
          {tab === 'requisition' && (
            <FlowStep number={1} title="HR check" isSelected={selected.kind === 'hr'} isLocked onSelect={() => setSelected({ kind: 'hr' })}>
              <ApproverChip label="Any HR" isRole />
            </FlowStep>
          )}
          {current.steps.map((step, index) => {
            const repeats = tab === 'requisition' && stepRepeatsHrCheck(step, hrCheckRoleId);
            return (
              <FlowStep
                key={index}
                number={stepNumberOf(tab, index)}
                title={step.name.trim() || 'Unnamed step'}
                isSelected={stepIndex === index}
                isSkipped={repeats}
                onSelect={() => setSelected({ kind: 'step', index })}
              >
                {step.approvers.map((approver, position) => (
                  <ApproverChip key={position} label={labelOf(approver, choices)} isRole={approver.kind === 'role' || approver.id === null} cannotDecide={cannotDecide(approver, choices)} />
                ))}
                {step.approvers.length > 1 && <Chip tone="warning">Parallel — all must approve</Chip>}
                {repeats && <Chip tone="neutral">Skipped: HR already checks first</Chip>}
              </FlowStep>
            );
          })}
          {tab === 'offer' && current.extraApprover && (
            <FlowStep title="Outside the pay band" isDashed isSelected={selected.kind === 'extra'} onSelect={() => setSelected({ kind: 'extra' })}>
              <span className={styles.stepNote}>Only when the offer is outside the job's band</span>
              <ApproverChip label={labelOf(current.extraApprover, choices)} isRole={current.extraApprover.kind === 'role'} cannotDecide={cannotDecide(current.extraApprover, choices)} />
            </FlowStep>
          )}
          <li className={styles.flowItem}>
            <ArrowRight size={18} className={styles.arrow} aria-hidden />
            <span className={`${styles.endpoint} ${styles.approved}`}>
              <Check size={16} aria-hidden /> Approved
            </span>
          </li>
        </ol>
        {canEdit && (
          <div className={styles.flowActions}>
            <Button
              size="sm"
              variant="outline"
              icon={Plus}
              disabled={current.steps.length >= 10}
              onClick={() => {
                onChange(addStep(current));
                setSelected({ kind: 'step', index: current.steps.length });
              }}
            >
              Add a step
            </Button>
          </div>
        )}
      </section>

      {selected.kind === 'hr' && <HrCheckCard />}
      {stepAt && (
        <StepEditor
          tab={tab}
          index={stepIndex}
          count={current.steps.length}
          step={stepAt}
          choices={choices}
          hrCheckRoleId={hrCheckRoleId}
          isLoadingChoices={approvers.isPending}
          readOnly={readOnly}
          onChange={(step) => onChange(replaceStep(current, stepIndex, step))}
          onMove={(by) => {
            onChange(moveStep(current, stepIndex, by));
            setSelected({ kind: 'step', index: stepIndex + by });
          }}
          onRemove={() => {
            onChange(removeStep(current, stepIndex));
            setSelected({ kind: 'step', index: Math.max(0, stepIndex - 1) });
          }}
        />
      )}
      {selected.kind === 'extra' && current.extraApprover && (
        <Card title="Outside the pay band" meta="Added after the last step, only when an offer is outside the job's band">
          <ApproverRow
            approver={current.extraApprover}
            choices={choices}
            isLoadingChoices={approvers.isPending}
            readOnly={readOnly}
            label="Extra approver"
            onChange={(extraApprover) => onChange({ ...current, extraApprover })}
          />
        </Card>
      )}

      <div className={styles.saveBar}>
        <span className={styles.saveNote}>
          {isStale
            ? 'Someone else saved this chain while you were changing it.'
            : canEdit && isDirty && missing
              ? missing
              : `${tab === 'requisition' ? 'Requests' : 'Offers'} already in approval keep the steps they started with.`}
        </span>
        {canEdit && isStale && (
          <Button variant="outline" onClick={onDiscard}>
            Load theirs
          </Button>
        )}
        {canEdit && isDirty && !isStale && (
          <Button variant="ghost" onClick={onDiscard}>
            {isNew ? 'Cancel' : 'Discard changes'}
          </Button>
        )}
        {canEdit && (
          <Button variant="solid" disabled={!isDirty || missing !== null || isStale} isPending={save.isPending} onClick={() => void submit()}>
            Save chain
          </Button>
        )}
      </div>
    </div>
  );
}

function FlowStep({
  number,
  title,
  isSelected,
  isLocked = false,
  isDashed = false,
  isSkipped = false,
  onSelect,
  children,
}: {
  readonly number?: number;
  readonly title: string;
  readonly isSelected: boolean;
  readonly isLocked?: boolean;
  readonly isDashed?: boolean;
  readonly isSkipped?: boolean;
  readonly onSelect: () => void;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <li className={styles.flowItem}>
      <ArrowRight size={18} className={styles.arrow} aria-hidden />
      <button
        type="button"
        className={styles.stepCard}
        data-selected={isSelected || undefined}
        data-dashed={isDashed || undefined}
        data-skipped={isSkipped || undefined}
        aria-pressed={isSelected}
        onClick={onSelect}
      >
        <span className={styles.stepHead}>
          {number !== undefined && <span className={styles.stepNumber}>Step {number}</span>}
          <span className={styles.stepTitle}>{title}</span>
          {isLocked && <Lock size={14} aria-label="Always first" className={styles.lock} />}
        </span>
        {children}
      </button>
    </li>
  );
}

function ApproverChip({ label, isRole, cannotDecide = false }: { readonly label: string; readonly isRole: boolean; readonly cannotDecide?: boolean }): ReactNode {
  return (
    <span className={styles.approver} data-problem={cannotDecide || undefined}>
      {isRole ? (
        <span className={styles.roleIcon} aria-hidden>
          <Users size={14} />
        </span>
      ) : (
        <Avatar name={label} size="sm" />
      )}
      <span className={styles.approverText}>
        <span className={styles.approverName}>{label}</span>
        <span className={styles.approverKind}>{cannotDecide ? "Can't decide approvals" : isRole ? 'Role · the first to act decides' : 'Person'}</span>
      </span>
    </span>
  );
}

function HrCheckCard(): ReactNode {
  return (
    <Card title="Step 1 · HR check" meta="Always first, on every hire request">
      <p className={styles.muted}>
        Every hire request starts here, chain or no chain. Anyone in HR (Admins too) reads it, can change anything unclear, and sees and
        settles its Jev budget. Nobody checks their own request. This step can't be moved or removed; the chain's steps follow it, in
        place of Leadership.
      </p>
    </Card>
  );
}

function StepEditor({
  tab,
  index,
  count,
  step,
  choices,
  hrCheckRoleId,
  isLoadingChoices,
  readOnly,
  onChange,
  onMove,
  onRemove,
}: {
  readonly tab: ChainTab;
  readonly index: number;
  readonly count: number;
  readonly step: DraftStep;
  readonly choices: ChainApproverChoices | null;
  readonly hrCheckRoleId: string | null;
  readonly isLoadingChoices: boolean;
  readonly readOnly: boolean;
  readonly onChange: (step: DraftStep) => void;
  readonly onMove: (by: -1 | 1) => void;
  readonly onRemove: () => void;
}): ReactNode {
  const repeats = tab === 'requisition' && stepRepeatsHrCheck(step, hrCheckRoleId);
  return (
    <section className={styles.editor} aria-label={`Step ${stepNumberOf(tab, index)}`}>
      <div className={styles.editorHead}>
        <span className={styles.stepNumber}>Step {stepNumberOf(tab, index)}</span>
        <h2 className={styles.editorTitle}>{step.name.trim() || 'Unnamed step'}</h2>
        {!readOnly && (
          <span className={styles.editorTools}>
            <Button size="sm" variant="ghost" icon={ArrowLeft} disabled={index === 0} onClick={() => onMove(-1)}>
              Move left
            </Button>
            <Button size="sm" variant="ghost" icon={ArrowRight} disabled={index === count - 1} onClick={() => onMove(1)}>
              Move right
            </Button>
            <Button size="sm" variant="ghost" icon={Trash2} disabled={count === 1} onClick={onRemove}>
              Remove
            </Button>
          </span>
        )}
      </div>
      {repeats && (
        <p className={styles.notice} role="status">
          This step only asks HR, who already check every request first, so requests skip it. Choose someone else, or remove it.
        </p>
      )}
      <div className={styles.editorGrid}>
        <Field label="Step name" isRequired>
          {({ inputId }) => <Input id={inputId} value={step.name} disabled={readOnly} onChange={(event) => onChange({ ...step, name: event.target.value })} />}
        </Field>
        <div className={styles.approverRows}>
          <span className={styles.rowsLabel}>{step.approvers.length > 1 ? 'Approvers — all must approve' : 'Approver'}</span>
          {step.approvers.map((approver, position) => (
            <div key={position} className={styles.approverEdit}>
              <ApproverRow
                approver={approver}
                choices={choices}
                isLoadingChoices={isLoadingChoices}
                readOnly={readOnly}
                label={`Approver ${position + 1}`}
                onChange={(next) => onChange(replaceApprover(step, position, next))}
              />
              {!readOnly && step.approvers.length > 1 && (
                <Button size="sm" variant="ghost" icon={Trash2} onClick={() => onChange(removeApprover(step, position))}>
                  Remove
                </Button>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button size="sm" variant="ghost" icon={Plus} disabled={step.approvers.length >= 10} onClick={() => onChange(addApprover(step))}>
              Add a parallel approver
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

/** A role or a person. Only those who can decide such a step are offered (a request can't get stuck on one nobody may decide). */
function ApproverRow({
  approver,
  choices,
  isLoadingChoices,
  readOnly,
  label,
  onChange,
}: {
  readonly approver: DraftApprover;
  readonly choices: ChainApproverChoices | null;
  readonly isLoadingChoices: boolean;
  readonly readOnly: boolean;
  readonly label: string;
  readonly onChange: (approver: DraftApprover) => void;
}): ReactNode {
  const problem = cannotDecide(approver, choices);
  const roleOptions = withSaved((choices?.roles ?? []).map((role) => ({ value: role.id, label: `Any ${role.label}` })), approver.kind === 'role' ? approver.id : null, approver.label ?? undefined);
  const people = choices?.people.map((person) => ({ id: person.id, fullName: person.fullName, email: person.email })) ?? [];
  const peopleWithSaved =
    approver.kind === 'user' && approver.id && !people.some((person) => person.id === approver.id) ? [...people, { id: approver.id, fullName: approver.label ?? 'A former colleague' }] : people;
  return (
    <div className={styles.approverRow}>
      <Select
        label={`${label} type`}
        disabled={readOnly}
        value={approver.kind}
        options={[
          { value: 'role', label: 'A role' },
          { value: 'user', label: 'A person' },
        ]}
        onChange={(kind) => onChange({ kind: kind === 'role' ? 'role' : 'user', id: null, label: null })}
      />
      {approver.kind === 'role' ? (
        <Select
          label={label}
          placeholder={isLoadingChoices ? 'Loading roles…' : 'Choose a role'}
          disabled={readOnly}
          isInvalid={problem}
          value={approver.id}
          options={roleOptions}
          onChange={(id) => onChange({ kind: 'role', id, label: roleOptions.find((option) => option.value === id)?.label ?? null })}
        />
      ) : (
        <PersonPicker
          label={label}
          placeholder={isLoadingChoices ? 'Loading people…' : 'Choose a person'}
          disabled={readOnly}
          isInvalid={problem}
          people={peopleWithSaved}
          value={approver.id}
          onChange={(id) => onChange({ kind: 'user', id, label: peopleWithSaved.find((person) => person.id === id)?.fullName ?? null })}
        />
      )}
      {problem && <p className={styles.problem}>{approver.label ?? 'This approver'} can't decide these approvals any more. Choose someone who can, or the chain can't be saved.</p>}
    </div>
  );
}

// --- Helpers --------------------------------------------------------------------------------------------------------

/** The HR role, read from a saved step the server marked as repeating the HR check; null when none is. */
function hrRoleOf(chain: ApprovalChainView | null): string | null {
  return chain?.steps.find((step) => step.repeatsHrCheck)?.approvers[0]?.approverRoleId ?? null;
}

/** The saved value stays choosable even when it is no longer offered (an archived venture, an approver who lost the right). */
function withSaved(options: readonly { value: string; label: string }[], value: string | null, label: string | undefined): readonly { value: string; label: string }[] {
  if (value === null || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: label ?? 'No longer available' }];
}

function labelOf(approver: DraftApprover, choices: ChainApproverChoices | null): string {
  if (approver.id === null) return approver.kind === 'role' ? 'Choose a role' : 'Choose a person';
  if (approver.kind === 'role') {
    const role = choices?.roles.find((candidate) => candidate.id === approver.id);
    return role ? `Any ${role.label}` : (approver.label ?? 'A role');
  }
  return choices?.people.find((person) => person.id === approver.id)?.fullName ?? approver.label ?? 'A former colleague';
}

/** True only once the choices are known and this approver isn't among them. */
function cannotDecide(approver: DraftApprover, choices: ChainApproverChoices | null): boolean {
  if (choices === null || approver.id === null) return false;
  return approver.kind === 'role' ? !choices.roles.some((role) => role.id === approver.id) : !choices.people.some((person) => person.id === approver.id);
}

/** The overall "Each approval step" limit for this venture (its own rule, else every venture's), in words; null while Time limits is off. */
function stepLimit(rules: readonly SlaRuleView[] | undefined, ventureId: string | null): string | null {
  const approvalRules = (rules ?? []).filter((rule) => rule.scopeKey === SLA_SCOPES.APPROVAL);
  const rule = approvalRules.find((candidate) => candidate.ventureId === ventureId) ?? approvalRules.find((candidate) => candidate.ventureId === null);
  return rule ? formatActive(rule.targetWorkingHours * 60) : null;
}
